import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { BridgeReturnDialog, type BridgeReturnPayload } from "@/components/BridgeReturnDialog";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { ProfileProvider, useProfile } from "@/contexts/ProfileContext";
import { useAuth } from "@/hooks/use-auth";
import { hasEnteredInterface } from "@/lib/entered";
import { captureReferralFromUrl, flushReferral } from "@/lib/referral";
import { initGA4, trackPageView } from "@/lib/analytics";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { OnboardingFlow } from "@/components/OnboardingFlow";
import { Loader2, AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import NotFound from "@/pages/not-found";
import SellerJoin from "@/pages/seller/join";
import SellerWelcome from "@/pages/seller/welcome";
import SellerEarnings from "@/pages/seller/earnings";
import MemnoraChat from "@/pages/memnora-chat";

const TemplesHistory = lazy(() => import("@/pages/temples-history"));
const MemoryDashboard = lazy(() => import("@/pages/memory-dashboard"));
const QaPage = lazy(() => import("@/pages/qa"));
const AnalyticsDashboard = lazy(() => import("@/pages/analytics-dashboard"));
const AuditDashboard = lazy(() => import("@/pages/audit-dashboard"));
const PlayReconciliationRuns = lazy(() => import("@/pages/play-reconciliation-runs"));
const PlayResync = lazy(() => import("@/pages/play-resync"));
const BibleStudyK12 = lazy(() => import("@/pages/bible-study-k12"));
const BibleStudyAdult = lazy(() => import("@/pages/bible-study-adult"));
const ConvergenceDashboard = lazy(() => import("@/pages/convergence-dashboard"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy-policy"));
const TermsOfService = lazy(() => import("@/pages/terms-of-service"));
const Support = lazy(() => import("@/pages/support"));
const AccountSecurity = lazy(() => import("@/pages/account-security"));
const SacredText = lazy(() => import("@/pages/sacred-text"));
const AuthPage = lazy(() => import("@/pages/auth-page"));
const GivingReceipts = lazy(() => import("@/pages/giving-receipts"));
const GivingAnalytics = lazy(() => import("@/pages/giving-analytics"));
const PaymentSuccess = lazy(() => import("@/pages/payment-success"));
const VerifyEmail = lazy(() => import("@/pages/verify-email"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const Landing = lazy(() => import("@/pages/landing"));

const JoinSchool = lazy(() => import("@/pages/school/join-school"));
const CreateSchool = lazy(() => import("@/pages/school/create-school"));
const AdminDashboard = lazy(() => import("@/pages/school/admin-dashboard"));
const TeacherDashboard = lazy(() => import("@/pages/school/teacher-dashboard"));
const TeachersRoles = lazy(() => import("@/pages/school/teachers-roles"));
const ClassesPage = lazy(() => import("@/pages/school/classes-page"));
const ClassroomAnalytics = lazy(() => import("@/pages/school/classroom-analytics"));
const CurriculumPage = lazy(() => import("@/pages/school/curriculum-page"));
const ResourcesPage = lazy(() => import("@/pages/school/resources-page"));
const AccessCodesPage = lazy(() => import("@/pages/school/access-codes-page"));
const SchoolSettingsPage = lazy(() => import("@/pages/school/school-settings"));
const SessionReportPage = lazy(() => import("@/pages/school/session-report"));
const TeacherWorkbook = lazy(() => import("@/pages/school/teacher-workbook"));

function SuspenseFallback() {
  return (
    <div className="h-screen w-full flex items-center justify-center">
      <div className="text-muted-foreground">Loading...</div>
    </div>
  );
}

function SchoolRoleRedirect({ schoolId }: { schoolId: string }) {
  const { data: schools, isLoading } = useQuery<Array<{ id: string; membership: { role: string } }>>({
    queryKey: ["/api/schools/my-schools"],
  });

  if (isLoading) {
    return <SuspenseFallback />;
  }

  const school = schools?.find(s => s.id === schoolId);
  const role = school?.membership?.role;
  const isAdmin = role === "ADMIN" || role === "CURRICULUM_LEAD";
  return <Redirect to={`/school/${schoolId}/${isAdmin ? "admin" : "teaching"}`} />;
}

function PageTracker({ onBridgeReturn }: { onBridgeReturn: (payload: BridgeReturnPayload) => void }) {
  const [location] = useLocation();
  useEffect(() => {
    trackPageView(location);
  }, [location]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bridgeSession = params.get("bridge_session");
    if (bridgeSession) {
      fetch("/api/bridge/return", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: bridgeSession }),
      })
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (data && data.ok) {
            onBridgeReturn({
              category: data.category ?? null,
              poBoxCode: data.poBoxCode ?? null,
              poBoxLabel: data.poBoxLabel ?? null,
              receiptId: data.receiptId ?? null,
              isGuest: !!data.isGuest,
              message: data.message ?? "Your offering has been received.",
            });
          }
        })
        .catch(() => {});
      params.delete("bridge_session");
      const clean = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (clean ? `?${clean}` : ""));
    }
  }, [onBridgeReturn]);

  return null;
}

function ReconnectingIndicator() {
  const { isReconnecting, reconnectFailed, dismissReconnectFailed } = useProfile();
  const [, navigate] = useLocation();

  if (reconnectFailed) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[9999] flex max-w-xs items-start gap-3 rounded-lg border border-amber-500/30 bg-background/95 px-4 py-3 text-sm text-foreground shadow-lg backdrop-blur"
        role="alert"
        aria-live="assertive"
        data-testid="status-reconnect-failed"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground">
            We couldn't reconnect your session. You can keep browsing as a guest or sign in again.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            onClick={() => {
              dismissReconnectFailed();
              navigate("/login");
            }}
            data-testid="button-reconnect-signin"
          >
            Sign in again
          </Button>
        </div>
        <button
          type="button"
          onClick={dismissReconnectFailed}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
          data-testid="button-reconnect-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (!isReconnecting) return null;
  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] flex items-center gap-2 rounded-full border border-amber-500/30 bg-background/90 px-4 py-2 text-sm text-muted-foreground shadow-lg backdrop-blur"
      role="status"
      aria-live="polite"
      data-testid="status-reconnecting"
    >
      <Loader2 className="h-4 w-4 animate-spin text-amber-400" />
      <span>Reconnecting your session…</span>
    </div>
  );
}

/**
 * "/" is the landing page for people who have never been here, and the
 * conversation for everyone else. A visitor counts as returning if they have an
 * account, a device profile, or have clicked into the interface before — the
 * pitch shouldn't stand between a returning user and their conversation.
 */
function RootRoute() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { profile } = useProfile();

  if (authLoading) return <SuspenseFallback />;
  if (isAuthenticated || profile || hasEnteredInterface()) return <MemnoraChat />;
  return <Landing />;
}

/** Mounts the first-run onboarding dialog only for a signed-in account that hasn't completed it yet. */
function OnboardingGate() {
  const { user } = useAuth();
  if (!user || user.onboardingCompletedAt) return null;
  return <OnboardingFlow key={user.id} user={user} />;
}

function AppContent() {
  const { isLoading, profile } = useProfile();
  const [bridgeReturnPayload, setBridgeReturnPayload] = useState<BridgeReturnPayload | null>(null);
  const [bridgeReturnOpen, setBridgeReturnOpen] = useState(false);

  useEffect(() => {
    initGA4();
    // Remember ?ref= on arrival; send it once there's an account to attach it
    // to. Both calls no-op when there's nothing to do.
    captureReferralFromUrl();
  }, []);

  // Retry attribution whenever a profile appears — a referral is captured
  // before signup, so the first attempt on page load usually has no account
  // to attach it to yet.
  useEffect(() => {
    void flushReferral();
  }, [profile?.id]);

  const handleBridgeReturn = useCallback((payload: BridgeReturnPayload) => {
    setBridgeReturnPayload(payload);
    setBridgeReturnOpen(true);
  }, []);

  if (isLoading) {
    return <SuspenseFallback />;
  }
  
  return (
    <SidebarProvider defaultOpen={false} className="!h-full">
      <AppSidebar />
      <SidebarInset className="flex flex-col overflow-y-auto min-h-0">
        <PageTracker onBridgeReturn={handleBridgeReturn} />
        <BridgeReturnDialog
          open={bridgeReturnOpen}
          onOpenChange={setBridgeReturnOpen}
          payload={bridgeReturnPayload}
        />
        <OnboardingGate />
        <Suspense fallback={<SuspenseFallback />}>
          <Switch>
            <Route path="/" component={RootRoute} />
            <Route path="/pricing" component={Landing} />
            <Route path="/chat" component={MemnoraChat} />
            <Route path="/memnora" component={MemnoraChat} />
            <Route path="/home" component={MemnoraChat} />
            <Route path="/temples" component={TemplesHistory} />
            <Route path="/history" component={TemplesHistory} />
            <Route path="/gallery">{() => <Redirect to="/memory" />}</Route>
            <Route path="/memory" component={MemoryDashboard} />
            <Route path="/qa" component={QaPage} />
            <Route path="/bible-study/k12" component={BibleStudyK12} />
            <Route path="/bible-study/adult" component={BibleStudyAdult} />
            <Route path="/sacred-text" component={SacredText} />
            <Route path="/seller/join" component={SellerJoin} />
            <Route path="/seller/welcome" component={SellerWelcome} />
            <Route path="/seller/earnings" component={SellerEarnings} />
            <Route path="/giving/receipts" component={GivingReceipts} />
            <Route path="/giving/analytics" component={GivingAnalytics} />
            <Route path="/command-center" component={GivingAnalytics} />
            <Route path="/war-room" component={GivingAnalytics} />
            <Route path="/privacy" component={PrivacyPolicy} />
            <Route path="/terms" component={TermsOfService} />
            <Route path="/support" component={Support} />
            <Route path="/analytics" component={AnalyticsDashboard} />
            <Route path="/convergence" component={ConvergenceDashboard} />
            <Route path="/audit" component={AuditDashboard} />
            <Route path="/admin/play-reconciliation" component={PlayReconciliationRuns} />
            <Route path="/admin/play-resync" component={PlayResync} />
            <Route path="/school/join" component={JoinSchool} />
            <Route path="/school/create" component={CreateSchool} />
            <Route path="/school/:schoolId">{(params) => <SchoolRoleRedirect schoolId={params.schoolId} />}</Route>
            <Route path="/school/:schoolId/admin" component={AdminDashboard} />
            <Route path="/school/:schoolId/teachers" component={TeachersRoles} />
            <Route path="/school/:schoolId/classes" component={ClassesPage} />
            <Route path="/school/:schoolId/analytics" component={ClassroomAnalytics} />
            <Route path="/school/:schoolId/classes/:classId/session-report/:reportId" component={SessionReportPage} />
            <Route path="/school/:schoolId/classes/:classId/session-report" component={SessionReportPage} />
            <Route path="/school/:schoolId/curriculum" component={CurriculumPage} />
            <Route path="/school/:schoolId/resources" component={ResourcesPage} />
            <Route path="/school/:schoolId/access-codes" component={AccessCodesPage} />
            <Route path="/school/:schoolId/settings" component={SchoolSettingsPage} />
            <Route path="/school/:schoolId/teaching" component={TeacherDashboard} />
            <Route path="/school/:schoolId/workbook" component={TeacherWorkbook} />
            <Route path="/settings">{() => { window.location.replace("/"); return null; }}</Route>
            <Route path="/account/security" component={AccountSecurity} />
            <Route path="/payment-success" component={PaymentSuccess} />
            <Route path="/verify-email" component={VerifyEmail} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/login">{() => <AuthPage defaultTab="login" />}</Route>
            <Route path="/register">{() => <AuthPage defaultTab="register" />}</Route>
            <Route path="/signin">{() => <Redirect to="/login" />}</Route>
            <Route path="/sign-in">{() => <Redirect to="/login" />}</Route>
            <Route path="/signup">{() => <Redirect to="/register" />}</Route>
            <Route path="/sign-up">{() => <Redirect to="/register" />}</Route>
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </SidebarInset>
    </SidebarProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="memnora-theme">
        <TooltipProvider>
          <ProfileProvider>
            <AppContent />
          </ProfileProvider>
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
